import styled from 'styled-components/macro';
import {
  BoxProps,
  colors,
  dimensions,
  ToolBar
} from '@bettermarks/bm-ui-components';
import React, {
  GetDerivedStateFromProps,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';
import {ReplaySubject, Subscription} from 'rxjs';
import {Route, Routes, useLocation} from 'react-router-dom';
import {useRoutes} from '../services/route.service';
import {useScrolling} from '../services/scrolling.service';
import {useStorage} from '../services/pt-storage.service';
import {MainNavigation} from './MainNavigation';

import AppSidebar from './AppComponents/AppSidebar';
import {
  chakra,
  Flex,
  Box,
  HStack,
  Heading,
  useColorModeValue
} from '@chakra-ui/react';
import {motion, isValidMotionProp, MotionProps} from 'framer-motion';
import {AppToolbar} from './AppComponents/AppToolbar';
import {ContentHeader} from './AppComponents/ContentHeader';

export const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  margin: 0 auto;
  background: ${colors.cWhite};
  min-height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

export const MainHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 0;
`;

export const Grid = styled.div<{
  padding?: boolean;
  maxCols?: number;
  maxWidth?: number;
}>`
  /* using css grid to adapt to the max possible number of columns for each viewport width without the need of media queries */
  --n: ${p => p.maxCols || 4};
  --g: ${dimensions.spaceM};
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(
      max(
        ${p => p.maxWidth || 250}px,
        (100% - (var(--n) - 1) * ${dimensions.spaceM}) / var(--n)
      ),
      1fr
    )
  );
  grid-gap: ${dimensions.spaceXl} ${dimensions.spaceM};
  padding: ${p => (p.padding ? `${dimensions.spaceM}` : 0)};
  & > h1 {
    margin: 0;
  }
`;

export const Group: any = styled.section<{
  disablePadding?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  p {
    margin: 0;
  }

  & > & {
    padding: 0;
  }

  ${Grid} + ${Grid} {
    margin-top: ${dimensions.spaceXl};
  }
`;

const animationConfiguration = {
  initial: {opacity: 0},
  animate: {opacity: 1},
  exit: {opacity: 0},
  transition: {
    duration: 0.5,
    ease: 'easeOut'
  }
};

const pageReveal = {
  initial: {opacity: 0, y: -20},
  animate: {opacity: 1, y: 0},
  exit: {opacity: 0, y: -20},
  transition: {
    duration: 0.25,
    delay: 0.15,
    ease: 'easeInOut'
  }
};

const MainScroll = styled(motion.div)<{
  paddingBottom?: boolean;
  paddingSelection?: string;
}>`
  overflow-y: auto;
  padding: ${p => p.paddingSelection || '24px 32px'};

  height: 100vh;

  flex: 1;
  position: relative;
  ${Group} + ${Group} {
    margin-top: ${dimensions.spaceXl};
  }

  .hidden {
    display: none;
  }
`;

export const TextModule = styled.div`
  display: flex;
  padding: 16px 0 16px 36px;
  p {
    padding: 0;
    margin: 0;
    line-height: 26px;
    flex: 1 1 auto;
  }

  img {
    max-width: 100%;
    align-self: center;
    flex: 0 0 auto;
  }

  @media (min-width: 768px) {
    p + img {
      margin-left: ${dimensions.spaceM};
    }
  }
  @media (max-width: 767px) {
    p + img {
      margin-top: ${dimensions.spaceM};
    }

    flex-direction: column;
  }
`;

const LayoutHeaderSubject = new ReplaySubject<ReactElement[]>(1);

export const LayoutComponentService = {
  set header(components: ReactElement[]) {
    LayoutHeaderSubject.next(components);
  }
};

export const HeaderElements: React.FC = () => {
  const [headerElements, setHeader] = useState<ReactElement[]>([]);
  useEffect(() => {
    const subscr = LayoutHeaderSubject.subscribe(elems => setHeader(elems));
    return () => subscr.unsubscribe();
  }, []);
  return <>{headerElements}</>;
};

const MotionContainer = motion<Omit<BoxProps, 'transition'>>(Box);

const ContentContainer = styled(motion.div)`
  width: 100%;
  max-width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Main = chakra(motion.main, {
  shouldForwardProp: isValidMotionProp
});

export const Layout: React.FC<
  PropsWithChildren<{
    customToolbar?: any;
    pageTitle?: string;
    paddingBottom?: boolean;
    isSelectionPage?: boolean;
    libraryName?: string;
  }>
> = ({
  customToolbar,
  pageTitle,
  paddingBottom,
  isSelectionPage,
  libraryName,
  children
}) => {
  const MainRef = useRef(null);
  const {pathname} = useLocation();

  const RouterService = useRoutes();
  const ScrollingService = useScrolling();
  const StorageService = useStorage();
  const scrollPosKey = `SCROLL_POS__${pathname}`;
  let scrollSubscriptions: Subscription[] = [];

  useEffect(() => {
    if (MainRef.current) {
      const main: HTMLElement = MainRef.current;
      const scrollTop = StorageService.load<string>(scrollPosKey) || '0';
      setTimeout(() => (main.scrollTop = parseInt(scrollTop)));
      scrollSubscriptions =
        (RouterService.routeShouldRestoreScrollPos(pathname) && [
          ScrollingService.handleScroll(
            main,
            window.location.href,
            scrollPosKey
          ),
          ScrollingService.scrollingPosition$().subscribe(
            () => (main.scrollTop = 0)
          )
        ]) ||
        [];
    }

    return () => {
      scrollSubscriptions.forEach(s => s.unsubscribe());
    };
  }, [pathname]);

  const RouteService = useRoutes();
  const [librarySelectionLink, setLibSelection] = useState<string>('');

  useEffect(() => {
    setLibSelection(RouteService.librarySelection(StorageService.getVersion()));
  }, [libraryName, isSelectionPage]);

  const isSelectionView = window.location.pathname.includes(
    '/library/mathematik'
  );

  return (
    <Flex
      bg={useColorModeValue('gray.100', 'gray.900')}
      w='100%'
      maxW='1440'
      margin='0 auto'
      p='6'
      padding={0}
      borderLeft='1px'
      borderRight='1px'
      borderColor={useColorModeValue('gray.200', 'gray.700')}
    >
      <AppSidebar />
      <Flex w='100%' flexDir='column' height='100vh' overflow='hidden'>
        <AppToolbar />
        <ContentContainer {...animationConfiguration}>
          {/* {customToolbar} */}
          {isSelectionView ? <HeaderElements /> : ''}
          {pageTitle && <ContentHeader pageTitle={pageTitle} />}

          <MainScroll
            {...pageReveal}
            paddingSelection={'24px 32px 64px'}
            paddingBottom={false}
            ref={MainRef}
          >
            {children}
          </MainScroll>
        </ContentContainer>
      </Flex>
    </Flex>
  );
};

export const Content = styled.article`
  display: flex;
  flex-direction: column;
  flex: 1;
´
`;

export const StyledToolBar = styled(ToolBar)`
  border-bottom: 1px solid ${colors.cGray400};
`;
